/**
 * «Опасная зона» · окно 6 запуска — экран сброса обучения.
 *
 * ⚠ Экран БЕЗ пункта навигации: нижних кнопок по-прежнему пять (решение дизайн-ворот
 * и принятого прототипа Ф2.0). Сюда ведёт карточка в самом низу настроек — это и есть
 * первый слой защиты: «в глубине настроек, а не рядом с темой».
 *
 * Три слоя защиты от случайного нажатия (требование владельца 25.07):
 *   1. отдельный экран в глубине настроек;
 *   2. сначала выбрать КОНКРЕТНУЮ кнопку, потом ВПИСАТЬ СЛОВО — «Вы уверены?» мало;
 *   3. снимок и кнопка «вернуть как было» столько дней, сколько сказало ядро.
 *
 * ⛔ Ни одной своей цифры и ни одного своего правила: что потеряется, чем подтвердить
 * и сколько живёт откат — приезжает из `core/reset.py`. Тексты и падежи — в
 * `src/reset.ts`, они проверяются запуском (`node tools/check-reset.mjs`).
 */

import "../styles/danger.css";

import {
  BUTTON_LABEL, CONFIRM_LABEL, CONFIRM_FALLBACK, CONFIRM_WRONG, DANGER_HINT,
  DANGER_TITLE, KEEPS_ALWAYS, UNDO_LABEL, canReset, doneLine, keptLine, lossLine,
  undoLine, undoPromise, undoable, type ScopeKey,
} from "../reset";
import { doReset, doUndo, loadReset, state } from "../state";
import { esc } from "./feedback";
import { render } from "../router";
import { tap } from "../tg";

let loading = false;
/** Какая кнопка «раскрыта» под подтверждение. null = ни одна: поле просто не существует. */
let armed: string | null = null;
let typed = "";
let wrong = false;
let busy = false;
let done = "";

/** Сбросить экран в исходное — при каждом заходе, чтобы поле не хранило вчерашний ввод. */
export function enterDanger(): void {
  armed = null;
  typed = "";
  wrong = false;
  done = "";
  void reload();
}

async function reload(): Promise<void> {
  if (loading) return;
  loading = true;
  try {
    await loadReset();
  } finally {
    loading = false;
    render("danger");
  }
}

function scopeHtml(scope: { key: string; title: string; what: string; keeps: string },
                   phrase: string, ttl: number): string {
  const open = armed === scope.key;
  const loss = lossLine(scope.key as ScopeKey, state.reset?.lost ?? null);
  return (
    `<div class="card danger${open ? " armed" : ""}">` +
      `<p class="h">${esc(scope.title)}</p>` +
      `<p class="hsub">${esc(scope.what)}</p>` +
      (loss ? `<p class="lossline">${esc(loss)}</p>` : "") +
      `<p class="footnote">${esc(scope.keeps)}</p>` +
      (open
        ? '<div class="confirmbox">' +
            `<label class="clabel" for="cfm-${esc(scope.key)}">` +
              `${esc(CONFIRM_LABEL(phrase))}</label>` +
            `<input class="ansin cfmin" id="cfm-${esc(scope.key)}" autocomplete="off" ` +
              `autocapitalize="characters" spellcheck="false" ` +
              `placeholder="${esc(phrase)}" value="${esc(typed)}">` +
            (wrong ? `<p class="footnote warn">${esc(CONFIRM_WRONG)}</p>` : "") +
            `<p class="footnote">${esc(undoPromise(ttl))}</p>` +
            '<div class="askrow">' +
              `<button type="button" class="askbtn danger" data-doreset="${esc(scope.key)}"` +
                `${canReset(typed, phrase, busy) ? "" : " disabled"}>` +
                `${esc(BUTTON_LABEL)}</button>` +
              '<button type="button" class="askbtn no" data-disarm="1">Отмена</button>' +
            "</div>" +
          "</div>"
        : `<div class="askrow"><button type="button" class="askbtn ghost" ` +
          `data-arm="${esc(scope.key)}">${esc(BUTTON_LABEL)}…</button></div>`) +
    "</div>"
  );
}

function undoHtml(): string {
  const snaps = undoable(state.reset?.snapshots);
  if (!snaps.length) return "";
  return (
    '<div class="card undo">' +
      `<p class="h">${esc(UNDO_LABEL)}</p>` +
      snaps.map((s) =>
        '<div class="undorow">' +
          `<p class="hsub">${esc(undoLine(s))}</p>` +
          '<div class="askrow"><button type="button" class="askbtn yes" ' +
            `data-undo="${s.id}"${busy ? " disabled" : ""}>${esc(UNDO_LABEL)}</button></div>` +
        "</div>").join("") +
    "</div>"
  );
}

export function renderDanger(host: HTMLElement): void {
  const d = state.reset;
  const phrase = d?.confirm_phrase ?? CONFIRM_FALLBACK;
  const ttl = d?.ttl_days ?? 0;

  host.innerHTML =
    '<div class="two-col">' +
      '<div class="col-main">' +
        `<div class="greet"><h2>${esc(DANGER_TITLE)}</h2></div>` +
        `<p class="hsub dangerlead">${esc(DANGER_HINT)}</p>` +
        (done ? `<div class="card okline"><p class="h">${esc(done)}</p></div>` : "") +
        `<div class="card keeps"><p class="h">Что останется</p>` +
        `<p class="hsub">${esc(d ? keptLine(d.lost) : KEEPS_ALWAYS)}</p></div>` +
        (d
          ? d.scopes.map((s) => scopeHtml(s, phrase, ttl)).join("")
          : '<div class="stub"><p>' +
            (loading ? "Считаем, что именно ты потеряешь…"
                     : "Цифры не приехали — пока сбрасывать нечего и нечем.") +
            "</p></div>") +
        undoHtml() +
      "</div>" +
      '<div class="col-side">' +
        '<button type="button" class="askbtn no" data-go="settings">← В настройки</button>' +
      "</div>" +
    "</div>";

  host.querySelectorAll<HTMLElement>("[data-arm]").forEach((btn) => {
    btn.addEventListener("click", () => {
      tap(btn);
      armed = btn.dataset.arm ?? null;
      typed = "";
      wrong = false;
      done = "";
      render("danger");
      host.querySelector<HTMLInputElement>(".cfmin")?.focus();
    });
  });

  host.querySelector("[data-disarm]")?.addEventListener("click", (e) => {
    tap(e.target as HTMLElement);
    armed = null;
    typed = "";
    wrong = false;
    render("danger");
  });

  // ⭐ Кнопка загорается только на совпавшем слове — и пересчитывается на каждый символ.
  const field = host.querySelector<HTMLInputElement>(".cfmin");
  field?.addEventListener("input", () => {
    typed = field.value;
    wrong = false;
    const go = host.querySelector<HTMLButtonElement>("button[data-doreset]");
    if (go) go.disabled = !canReset(typed, phrase, busy);
  });

  host.querySelector("button[data-doreset]")?.addEventListener("click", (e) => {
    const btn = e.target as HTMLButtonElement;
    if (btn.disabled || busy) return;
    const scope = btn.dataset.doreset as string;
    if (!canReset(typed, phrase)) {          // страховка: слово могли стереть после фокуса
      wrong = true;
      render("danger");
      return;
    }
    tap(btn);
    busy = true;
    render("danger");
    void doReset(scope, typed).then((title) => {
      busy = false;
      armed = null;
      typed = "";
      wrong = !title;                        // ядро отказало — говорим об этом честно
      done = title ? doneLine(title, ttl) : "";
      render("danger");
    });
  });

  host.querySelectorAll<HTMLElement>("button[data-undo]").forEach((btn) => {
    btn.addEventListener("click", () => {
      if (busy) return;
      tap(btn);
      busy = true;
      render("danger");
      void doUndo(Number(btn.dataset.undo)).then((title) => {
        busy = false;
        done = title ? `Вернули: «${title}».` : "";
        render("danger");
      });
    });
  });
}
